import React from 'react';
import SafeAvatar from '../SafeAvatar';
import { AVAILABLE_AREAS, AVAILABLE_ROLES } from './constants';

const getFullName = (member) => {
  if (!member) return '';
  if (member.firstName || member.lastName) {
    return `${member.firstName || ''} ${member.lastName || ''}`.trim();
  }
  return member.name || 'Unnamed Team Member';
};

const getInitials = (member) => {
  const first = member?.firstName?.[0] || member?.name?.[0] || '';
  const last = member?.lastName?.[0] || '';
  return `${first}${last}`.toUpperCase() || '?';
};

const getRoleLabel = (role) => (
  AVAILABLE_ROLES.find((availableRole) => availableRole.value === role)?.label || role || 'Team Member'
);

const getAreas = (member) => (
  (member?.areas || [])
    .map((area) => AVAILABLE_AREAS.find((availableArea) => availableArea.value === area)?.label || area)
);

const getTeams = (member) => {
  if (member?.userTeams?.length) {
    return member.userTeams
      .filter((userTeam) => userTeam.team)
      .map((userTeam) => ({
        id: userTeam.id,
        name: userTeam.team.name,
        color: userTeam.team.color,
        isPrimary: userTeam.isPrimary
      }));
  }

  if (member?.team) {
    return [{
      id: member.team.id,
      name: member.team.name,
      color: member.team.color,
      isPrimary: true
    }];
  }

  return [];
};

const formatPhoneHref = (phoneNumber) => {
  if (!phoneNumber) return null;
  const cleaned = phoneNumber.replace(/[^\d+]/g, '');
  return cleaned ? `tel:${cleaned}` : null;
};

const InfoRow = ({ label, value, href }) => (
  <div className="flex flex-col gap-1 border-b border-gray-100 px-4 py-3 last:border-b-0 sm:flex-row sm:items-center sm:justify-between">
    <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</dt>
    <dd className="min-w-0 text-sm text-gray-900 sm:text-right">
      {href && value ? (
        <a className="text-sky-700 hover:text-sky-900 hover:underline" href={href}>
          {value}
        </a>
      ) : (
        value || <span className="text-gray-500">Not listed</span>
      )}
    </dd>
  </div>
);

const TeamBadge = ({ team }) => (
  <span
    className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium"
    style={{
      backgroundColor: team.color ? `${team.color}1F` : '#F3F4F6',
      borderColor: team.color || '#D1D5DB',
      color: team.color || '#374151'
    }}
  >
    <span
      className="h-1.5 w-1.5 rounded-full"
      style={{ backgroundColor: team.color || '#6B7280' }}
    />
    {team.name}
    {team.isPrimary && <span className="font-semibold">Primary</span>}
  </span>
);

export default function TeamMemberProfilePanel({
  member,
  isOpen,
  onClose,
  canManageTeamMembers,
  onEdit,
  onToggleActive,
  onResendInvite,
  isResendingInvite,
  resendingMemberId
}) {
  if (!isOpen || !member) return null;

  const fullName = getFullName(member);
  const areas = getAreas(member);
  const teams = getTeams(member);
  const phoneHref = formatPhoneHref(member.phoneNumber);

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-gray-900/45" onClick={onClose} />

      <div className="absolute inset-y-0 right-0 flex max-w-full pl-0 sm:pl-10">
        <div className="w-screen max-w-xl">
          <div className="flex h-full flex-col overflow-y-auto bg-gray-50 shadow-xl">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white px-4 py-3">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Team Member</p>
                <h2 className="text-lg font-semibold text-gray-900">{fullName}</h2>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="rounded-full p-2 text-gray-500 transition hover:bg-gray-100 hover:text-gray-700"
                aria-label="Close profile"
              >
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                </svg>
              </button>
            </div>

            <div className="space-y-4 p-4">
              <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
                <div className="flex flex-col items-center text-center">
                  {member.avatarUrl ? (
                    <SafeAvatar
                      src={member.avatarUrl}
                      alt={fullName}
                      className="h-28 w-28 rounded-full ring-4 ring-white shadow-md"
                      size={112}
                      lazy={false}
                    />
                  ) : (
                    <span className="flex h-28 w-28 items-center justify-center rounded-full bg-sky-50 text-3xl font-semibold text-sky-700 ring-1 ring-sky-100">
                      {getInitials(member)}
                    </span>
                  )}

                  <h3 className="mt-4 text-xl font-semibold text-gray-900">{fullName}</h3>
                  <p className="mt-1 text-sm text-gray-600">{getRoleLabel(member.role)}</p>
                  <span
                    className={`mt-3 inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${
                      member.isActive ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {member.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>

                <div className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-3">
                  <a
                    href={`mailto:${member.email}`}
                    className="inline-flex items-center justify-center gap-2 rounded-md bg-sky-50 px-3 py-2 text-sm font-medium text-sky-700 transition hover:bg-sky-100"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Email
                  </a>
                  <a
                    href={phoneHref || undefined}
                    aria-disabled={!phoneHref}
                    className={`inline-flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition ${
                      phoneHref
                        ? 'bg-green-50 text-green-700 hover:bg-green-100'
                        : 'cursor-not-allowed bg-gray-50 text-gray-400'
                    }`}
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h2.28a1 1 0 01.95.68l1.06 3.18a1 1 0 01-.24 1.03l-1.27 1.27a16 16 0 006.06 6.06l1.27-1.27a1 1 0 011.03-.24l3.18 1.06a1 1 0 01.68.95V19a2 2 0 01-2 2h-1C9.82 21 3 14.18 3 6V5z" />
                    </svg>
                    Call
                  </a>
                  <a
                    href={phoneHref ? phoneHref.replace('tel:', 'sms:') : undefined}
                    aria-disabled={!phoneHref}
                    className={`inline-flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition ${
                      phoneHref
                        ? 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
                        : 'cursor-not-allowed bg-gray-50 text-gray-400'
                    }`}
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h8M8 14h5m-9 5l2.6-2.6A8 8 0 1119 10a8 8 0 01-8 8H7l-3 3z" />
                    </svg>
                    Text
                  </a>
                </div>
              </section>

              <section className="rounded-lg border border-gray-200 bg-white shadow-sm">
                <div className="border-b border-gray-100 px-4 py-3">
                  <h3 className="text-sm font-semibold text-gray-900">Contact Information</h3>
                </div>
                <dl>
                  <InfoRow label="Email" value={member.email} href={`mailto:${member.email}`} />
                  <InfoRow label="Phone" value={member.phoneNumber} href={phoneHref} />
                  <InfoRow label="Role" value={getRoleLabel(member.role)} />
                  <InfoRow label="Status" value={member.isActive ? 'Active' : 'Inactive'} />
                </dl>
              </section>

              <section className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                <h3 className="text-sm font-semibold text-gray-900">Areas</h3>
                <div className="mt-3 flex flex-wrap gap-2">
                  {areas.length ? (
                    areas.map((area) => (
                      <span key={area} className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700">
                        {area}
                      </span>
                    ))
                  ) : (
                    <span className="text-sm text-gray-500">No areas assigned</span>
                  )}
                </div>
              </section>

              {teams.length > 0 && (
                <section className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                  <h3 className="text-sm font-semibold text-gray-900">Teams</h3>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {teams.map((team) => <TeamBadge key={team.id} team={team} />)}
                  </div>
                </section>
              )}

              {canManageTeamMembers && (
                <section className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                  <h3 className="text-sm font-semibold text-gray-900">Admin Actions</h3>
                  <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                    <button
                      type="button"
                      onClick={() => onEdit(member)}
                      className="inline-flex flex-1 items-center justify-center rounded-md bg-sky-600 px-3 py-2 text-sm font-medium text-white transition hover:bg-sky-700"
                    >
                      Edit Team Member
                    </button>
                    {member.isActive ? (
                      <button
                        type="button"
                        onClick={() => onResendInvite(member)}
                        disabled={isResendingInvite && resendingMemberId === member.id}
                        className="inline-flex flex-1 items-center justify-center rounded-md border border-orange-200 bg-orange-50 px-3 py-2 text-sm font-medium text-orange-700 transition hover:bg-orange-100 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-50 disabled:text-gray-400"
                      >
                        {isResendingInvite && resendingMemberId === member.id ? 'Sending...' : 'Resend Invite'}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => onToggleActive(member)}
                        className="inline-flex flex-1 items-center justify-center rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm font-medium text-green-700 transition hover:bg-green-100"
                      >
                        Reactivate
                      </button>
                    )}
                  </div>
                </section>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
